import { EmptyDataMessage } from '@/common/components/EmptyDataMessage'
import ListFilters from '@/common/components/ListFilters'
import { Pagination } from '@/common/components/Table/pagination'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { CompaniesTransformer } from '@/features/companies/services/companiesTransformer'
import { useEstablishmentCompanies } from '@/features/establishments/hooks/useEstablishmentCompanies'
import { FROM_TYPE_FILTER_OPTIONS } from '@/features/tmaisUsers/constants/rewardLedgerEntries.constant'
import { useRewardLedgerEntries } from '@/features/tmaisUsers/hooks/useRewardLedgerEntries'
import { RewardLedgerEntryData } from '@/features/tmaisUsers/types/rewardLedgerEntries.type'
import { useState } from 'react'
import { RedemptionHistoryCard } from './redemptionHistory/RedemptionHistoryCard'
import { RedemptionHistoryCardSkeleton } from './redemptionHistory/RedemptionHistoryCardSkeleton'

interface RedemptionHistoryTabProps {
  userId: string
}

const RedemptionHistoryTab = ({ userId }: RedemptionHistoryTabProps) => {
  const { filterParams, setFilterParams } = useFilterParams()
  const [searchEstablishments, setSearchEstablishments] = useState('')

  const { data: establishmentOptions } = useEstablishmentCompanies({
    filters: { search: searchEstablishments, tmaisModuleEnabled: true },
    mapper: Mapper.mapWithTransformer({ transformer: CompaniesTransformer.optionsTransformer() }),
  })

  const {
    data: entries,
    meta,
    isPending,
  } = useRewardLedgerEntries<RewardLedgerEntryData[]>({
    userId,
    filters: {
      page: filterParams.page || 1,
      fromType: filterParams.fromType?.value,
      establishmentCompanyId: filterParams.establishmentCompanyId?.value,
    },
  })

  const hasEntries = !isPending && !!entries?.length

  return (
    <div className="flex flex-col gap-2">
      <ListFilters
        filters={[
          {
            placeholder: 'Tipo de registro',
            onChange: (fromType) => setFilterParams((prev) => ({ ...prev, fromType })),
            value: filterParams.fromType,
            options: FROM_TYPE_FILTER_OPTIONS,
          },
          {
            placeholder: 'Fornecedor',
            onInputChange: setSearchEstablishments,
            onChange: (establishment) =>
              setFilterParams((prev) => ({
                ...prev,
                establishmentCompanyId: establishment,
              })),
            value: filterParams.establishmentCompanyId,
            options: [
              { label: 'Todos', value: '' },
              { label: 'Taurus (Global)', value: 'null' },
              ...(establishmentOptions || []),
            ],
          },
        ]}
      />

      {isPending || hasEntries ? (
        <div>
          <div className="flex flex-col gap-3 overflow-y-auto max-h-[calc(100vh-290px)] pb-4">
            {isPending
              ? Array(5)
                  .fill(0)
                  .map((_, index) => <RedemptionHistoryCardSkeleton key={index} />)
              : entries?.map((entry) => <RedemptionHistoryCard key={entry.id} entry={entry} />)}
          </div>

          <div className="w-full px-4 py-2 bg-black-300 rounded-lg flex justify-between items-center sticky bottom-0">
            {typeof meta?.total !== 'undefined' && (
              <p className="text-xs italic font-light">
                Mostrando {entries?.length || 0} resultados de {meta.total}
              </p>
            )}

            <Pagination
              currentPage={Number(filterParams.page) || 1}
              totalPages={meta?.total_pages || 1}
              setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
            />
          </div>
        </div>
      ) : (
        <EmptyDataMessage className="max-h-[calc(100vh-232px)]" />
      )}
    </div>
  )
}

export default RedemptionHistoryTab
